import React, { useRef, type ReactElement } from "react";
import * as Sentry from "@sentry/react";
import { Sheet, SheetContent, SheetDescription, toast } from "@ndd/ui";
import { PATIENT_DETAIL_FLAG, RD_SCHEDULE_FLAG } from "../../../../shared/features";
import type { RdScheduleAppointment } from "../../../../shared/rd-schedule";
import { appointmentBlockSelector } from "../../lib/rd-schedule-week";
import type { ActionOptionsState } from "./actions/use-action-options";
import {
  AppointmentDetailsBody,
  type AppointmentDetailsActions,
  type FeeDecisionPort,
  type RenderPatientLink,
} from "./appointment-details-body";

// NO-875. The RD Schedule appointment details panel: a 420px right-side Sheet
// over the page. Self-contained: it is driven by one week appointment record
// and `onOpenChange`, so the Week view (NO-874) wires a block click to it and
// the action dialogs (NO-876) hang off `actions`. Nothing here reads the week.

/** The two flags the panel reads, from the server-evaluated flag map. */
export function panelFlags(flags: Readonly<Record<string, boolean>> | undefined): {
  rdScheduleEnabled: boolean;
  patientDetailEnabled: boolean;
} {
  return {
    rdScheduleEnabled: flags?.[RD_SCHEDULE_FLAG] === true,
    patientDetailEnabled: flags?.[PATIENT_DETAIL_FLAG] === true,
  };
}

/** Copies a link and confirms with a toast; a refused write says so. */
export async function copyClientLink(
  url: string,
  clipboard: Pick<Clipboard, "writeText"> | undefined = typeof navigator === "undefined"
    ? undefined
    : navigator.clipboard
): Promise<boolean> {
  try {
    if (!clipboard) throw new Error("clipboard unavailable");

    await clipboard.writeText(url);
    toast.success("Link copied");

    return true;
  } catch {
    // The message carries no link: a video link identifies a visit. The event
    // and message are in the shared scrubber's catalogs, so they stay readable.
    Sentry.captureException(new Error("rd_schedule_details_link_copy_failed"), {
      tags: {
        event: "rd_schedule.details_link_copy_failed",
        operation: "copy",
        source: "client",
        surface: "clipboard",
      },
    });
    toast.error("Couldn't copy the link. Select it and copy it by hand.");

    return false;
  }
}

/**
 * When the sheet closes, focus goes to the visit's block in the Week grid. A
 * panel reopened by Back or a save has no opener of its own to return to, so
 * the default would land on the page. Without that block, or when something
 * else already holds focus (a dialog opening), the default stands.
 */
export function returnFocusToBlock(
  event: Event,
  appointmentId: string | null,
  root: Pick<Document, "querySelector" | "activeElement"> = document
): void {
  if (appointmentId === null) return;

  const active = root.activeElement;
  const block = root.querySelector<HTMLElement>(
    appointmentBlockSelector(appointmentId)
  );

  if (!block || (active && active !== document.body)) return;

  event.preventDefault();
  block.focus();
}

export function AppointmentDetailsPanel(props: {
  /** Null while nothing is selected; the sheet stays closed. */
  appointment: RdScheduleAppointment | null;
  timezone: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The `rd-schedule` Project flag; the panel renders nothing while it is off. */
  rdScheduleEnabled: boolean;
  patientDetailEnabled: boolean;
  actions: AppointmentDetailsActions;
  fee?: FeeDecisionPort;
  /** The visit's note; the connected panel reads it when the panel opens. */
  notes?: ActionOptionsState;
  /** The note the RD just saved, while Healthie has not returned it yet. */
  savingNote?: string | null;
  onReloadNotes?: () => void;
  renderPatientLink?: RenderPatientLink;
  onCopyLink?: (url: string) => void;
}): ReactElement | null {
  const { rdScheduleEnabled } = props;
  if (!rdScheduleEnabled) return null;

  return <PanelSheet {...props} />;
}

type PanelProps = Parameters<typeof AppointmentDetailsPanel>[0];

function PanelSheet({
  appointment,
  timezone,
  open,
  onOpenChange,
  patientDetailEnabled,
  actions,
  fee,
  notes,
  savingNote,
  onReloadNotes,
  renderPatientLink,
  onCopyLink = (url) => void copyClientLink(url),
}: PanelProps): ReactElement {
  // The visit last shown: the sheet closes with `appointment` already null.
  const lastShown = useRef<string | null>(null);

  if (appointment) lastShown.current = appointment.id;

  return (
    <Sheet open={open && appointment !== null} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        // The body draws its own close in the header.
        className="w-full max-w-none gap-0 overflow-y-auto border-l border-border bg-card p-0 shadow-[-12px_0_32px_rgba(73,29,43,0.10)] sm:max-w-[420px] [&>button:last-child]:hidden"
        onCloseAutoFocus={(event) => returnFocusToBlock(event, lastShown.current)}
      >
        <SheetDescription className="sr-only">Appointment details</SheetDescription>
        {appointment ? (
          <AppointmentDetailsBody
            appointment={appointment}
            timezone={timezone}
            patientDetailEnabled={patientDetailEnabled}
            actions={actions}
            fee={fee}
            notes={notes}
            savingNote={savingNote}
            onReloadNotes={onReloadNotes}
            onCopyLink={onCopyLink}
            renderPatientLink={renderPatientLink}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
